import { ChangeEventHandler, FC, useMemo } from 'react';

import dayjs from 'dayjs';
import { Gift } from 'lucide-react';
import { isNullish } from 'remeda';
import useSWR from 'swr';

import { useRewardsHistorySearch } from './hooks';

import css from './Rewards.module.css';

import { userProfileService } from 'v2/api/userProfileService';
import { DataTable } from 'v2/components/dataTable/DataTable';
import { Loader } from 'v2/components/loader/Loader';
import { ProfileCard } from 'v2/components/profileCard/ProfileCard';
import { SearchTextField } from 'v2/components/searchTextField/SearchTextField';
import { usePagination } from 'v2/hooks/usePagination';
import { TableColumn } from 'v2/types';
import { UserWinRewardDto } from 'v2/types/UserProfileModel';

type RewardRow = UserWinRewardDto & { id: string };

interface RewardsProps {
  userId: string;
}

export const Rewards: FC<RewardsProps> = ({ userId }) => {
  const { page, pageSize, onPageChange } = usePagination({ defaultPageSize: 10 });

  const { searchValue, setSearchValue, swrParams } = useRewardsHistorySearch({ userId, onPageChange });

  const { data, isLoading } = useSWR(
    ['getUserWinRewards', page, pageSize, swrParams],
    userProfileService.getUserWinRewards,
    { keepPreviousData: true, revalidateIfStale: false }
  );

  const rows: RewardRow[] = useMemo(
    () =>
      data?.result.map((row, i) => ({
        ...row,
        id: `${row.date}-${row.value}-${i}`,
      })) ?? [],
    [data?.result]
  );

  const columns: TableColumn<RewardRow>[] = useMemo(
    () => [
      {
        key: 'name',
        label: 'Earned',
        render: (row) => row.value,
      },
      {
        key: 'receivedBy',
        label: 'Received By',
        render: (row) => row.receivedBy,
      },
      {
        key: 'date',
        label: 'Date',
        render: (row) => dayjs(row.date).format('YYYY-MM-DD'),
      },
    ],
    []
  );

  const handleSearchChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setSearchValue(e.target.value);
  };

  return (
    <ProfileCard className={css.rewardsCard} icon={<Gift width={18} height={18} />} title="Rewards history">
      {isLoading && isNullish(data) ? (
        <Loader />
      ) : (
        <>
          <div className={css.searchRow}>
            <SearchTextField
              className={css.searchField}
              value={searchValue}
              onChange={handleSearchChange}
              label="Reward title"
              onClear={() => setSearchValue('')}
            />
          </div>

          <DataTable
            columns={columns}
            data={rows}
            onPageChange={onPageChange}
            page={page}
            pageSize={pageSize}
            total={data?.total}
            withPagination
          />
        </>
      )}
    </ProfileCard>
  );
};
